/**
 * Same-device live editing.
 *
 * BroadcastChannel syncs tabs and windows within one browser on one machine.
 * That is the entire scope: there is no server, so there is no cross-device or
 * cross-network collaboration, and none is stubbed or implied. The interface is
 * small enough that a networked transport could be added later, but shipping a
 * half-wired one would misrepresent what the app does.
 */

import type { SlateElement } from '../scene/types';

export type SyncMessage =
  | {
      readonly kind: 'elements';
      readonly boardId: string;
      readonly clientId: string;
      readonly elements: readonly SlateElement[];
    }
  | {
      readonly kind: 'cursor';
      readonly boardId: string;
      readonly clientId: string;
      readonly x: number;
      readonly y: number;
    }
  | { readonly kind: 'hello'; readonly boardId: string; readonly clientId: string }
  | { readonly kind: 'bye'; readonly boardId: string; readonly clientId: string };

export interface SyncTransport {
  post(message: SyncMessage): void;
  subscribe(handler: (message: SyncMessage) => void): () => void;
  close(): void;
  readonly available: boolean;
}

/** Same-origin by construction, but a shape check still beats a crash. */
export function isSyncMessage(value: unknown): value is SyncMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  if (typeof m.boardId !== 'string' || typeof m.clientId !== 'string') return false;
  switch (m.kind) {
    case 'elements':
      return Array.isArray(m.elements);
    case 'cursor':
      return (
        typeof m.x === 'number' &&
        typeof m.y === 'number' &&
        Number.isFinite(m.x) &&
        Number.isFinite(m.y)
      );
    case 'hello':
    case 'bye':
      return true;
    default:
      return false;
  }
}

/** Used when BroadcastChannel is missing. The editor works exactly as well. */
export function createNullTransport(): SyncTransport {
  return {
    available: false,
    post() {},
    subscribe() {
      return () => {};
    },
    close() {},
  };
}

export function createBroadcastTransport(channelName: string): SyncTransport {
  if (typeof BroadcastChannel === 'undefined') return createNullTransport();
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(channelName);
  } catch {
    return createNullTransport();
  }
  const handlers = new Set<(message: SyncMessage) => void>();

  channel.onmessage = (event: MessageEvent) => {
    if (!isSyncMessage(event.data)) return;
    for (const handler of handlers) handler(event.data);
  };

  return {
    available: true,
    post(message) {
      try {
        channel.postMessage(message);
      } catch {
        // A closed channel or an unclonable payload. Local editing continues.
      }
    },
    subscribe(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    close() {
      handlers.clear();
      try {
        channel.close();
      } catch {
        // Already closed.
      }
    },
  };
}
